import { first } from 'lodash';

import { LT_POS } from 'bl/loggers.bl';
import determinePaymentType from 'bl/payment/determinePaymentType.bl';
import validateCheckoutData from 'bl/payment/validateCheckoutData.bl';
import { isPosEnabledStrictly } from 'bl/pos/checks.bl';
import FLAG from 'config/flag.singleton';
import LINK from 'constants/link.const';
import EMSG from 'constants/messages/errorMessage.const';
import { AP_STATUS } from 'constants/payment/appointmentStatus.const';
import S from 'constants/string.const';
import alen from 'utils/arrays/alen.util';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyToJsonClone, fromAnyToUglyJson } from 'utils/converters/json.util';
import { isEnvDev, isEnvQA, isEnvStage } from 'utils/env/determine.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import navFor from 'utils/links/navFor.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFn from 'utils/predicates/function/isFn.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

const CTAG = 'performTx()';
const L = logger([CTAG, LT_POS]);

const IS_STAGE = isEnvStage();
const IS_QA = isEnvQA();
const IS_DEV = isEnvDev();

/*
// Sample for chaos testing
// noinspection JSLastCommaInObjectLiteral
SLICK.flag.checkoutPaymentPopUpChaos = JSON.stringify({
  throwBeforeInitTxUpdateAppointment: false,
  throwBeforeInitTxCreateTransaction: false,
  throwBeforeCompleteTxChangeStatus: false,
  throwBeforeConfirmChargeCardOnFile: false,
  throwBeforeDetermineIntentId: false,
  failInitTxDataValidation: false,
  failPerformTxInit: false,
  failConfirmChargeCardOnFile: false,
  failDetermineIntentId: false,
  failCreateTransaction: false,
});
 */

export const chaosTrue = (name) => {
  if (!IS_STAGE && !IS_DEV && !IS_QA) return false;

  const stringified = fromAnyToTrimmed(name);
  if (stringified) return fromAnyToBool(FLAG.checkoutPaymentPopUpChaos?.[stringified]);

  L.error('chaos()', 'Invalid name:', name);
  return false;
};
export const chaosThrow = (name) => {
  if (!IS_STAGE && !IS_DEV && !IS_QA) return;

  const stringified = fromAnyToTrimmed(name);
  if (!stringified) L.error('chaos()', 'Invalid name:', name);

  if (FLAG.checkoutPaymentPopUpChaos?.[stringified]) throw new Error(`Chaos in ${stringified}`);
};

const M_FETCHING = 'aborting create transaction due to negative flag or already fetching';
const M_INVALID_DATA = 'aborting create transaction due to negative data validation';
const M_CREATE_TX_PASS = 'create transaction succeeded';
const M_CREATE_TX_FAIL = 'create transaction failed';

const initTx = async (options) => {
  const FTAG = 'initTx()';
  const tag = ct(FTAG, options?.tag);
  const t = options?.t;

  const checkpoint = options?.checkpoint;
  checkpoint('initTx.init', { ...asObject(options), tag });

  const isFetching = options?.isFetching;
  const isTxInitiatedRef = options?.isTxInitiatedRef;

  if (isFetching || isTxInitiatedRef?.current) {
    checkpoint('initTx.bail', { tag, isFetching, message: M_FETCHING, isTxInitiated: isTxInitiatedRef?.current });
    return false;
  }

  {
    const tag = 'initTx > markAsInitiated';
    const value = true;
    isTxInitiatedRef.current = value;
    checkpoint('isTxInitiated.yay', { tag, 'isTxInitiated.value': value });
  }

  const paymentTypeOptions = {
    checkoutOption: options?.checkoutOption,
    cardAmount: options?.cardAmountFromData,
    onlineAmount: options?.onlineAmountFromData,
    isPosEnabled: isPosEnabledStrictly(options?.posFlow),
  };
  const paymentType = determinePaymentType(paymentTypeOptions);
  checkpoint('initTx.paymentType.done', { tag, paymentType, paymentTypeOptions });

  call(`${FTAG} > feChangePaymentType()`, options?.feChangePaymentType, { paymentType });

  const procedures = asArray(options?.procedures);
  const servicesOfFirstClient = asArray(options?.data?.clients?.[0]?.services);
  const appointmentIds = asArray(options?.appointmentIds);
  const salonId = options?.salonId;

  // TODO: determine why this needs updated after both for cycles finish
  let filteredProcedures = void 1;
  // NOTE: depositListData might be set each cycle near the end of the big loop and is a shared mutable state
  let depositListData = void 1;

  for (const apId of appointmentIds) {
    const updatedProcedures = [];
    const checkedOutProcedures = procedures.filter(({ appointment: { id } }) => id === apId);

    // update procedure price for this appointment
    if (isFullArray(checkedOutProcedures)) {
      for (const checkedOutProcedure of checkedOutProcedures) {
        const checkedOutProcedureId = checkedOutProcedure.id;
        const checkedOutServiceId = checkedOutProcedure.service.id;

        filteredProcedures = procedures.filter(({ id }) => id === checkedOutProcedureId);
        if (isFullArray(filteredProcedures)) {
          const firstFilteredProcedure = first(filteredProcedures);
          const updatedProcedures = servicesOfFirstClient.filter(({ id }) => id === checkedOutServiceId);

          if (isFullArray(updatedProcedures)) {
            firstFilteredProcedure.price = first(updatedProcedures)?.price;

            if (!firstFilteredProcedure.parent_id) delete firstFilteredProcedure.parent_id;

            updatedProcedures.push(firstFilteredProcedure);
          } // if isFullArray updatedProcedure
        } // if isFullArray filteredProcedures
      } // for checkedOutProcedure

      const updatedProceduresIds = updatedProcedures.map(({ id }) => id);

      for (const checkedOutProcedure of checkedOutProcedures) {
        const checkedOutProcedureId = checkedOutProcedure.id;

        filteredProcedures = procedures.filter(({ id }) => id === checkedOutProcedureId);

        if (isFullArray(filteredProcedures)) {
          const firstFilteredProcedure = first(filteredProcedures);
          const firstFilteredProcedureId = firstFilteredProcedure.id;

          if (updatedProceduresIds.indexOf(firstFilteredProcedureId) < 0) {
            if (!firstFilteredProcedure.parent_id) delete firstFilteredProcedure.parent_id;

            updatedProcedures.push(firstFilteredProcedure);
          } // if updatedProceduresIds does not contain firstFilteredProcedureId
        } // if isFullArray filteredProcedures
      } // for checkedOutProcedure
    } // if isFullArray checkedOutProcedures

    if (filteredProcedures && filteredProcedures.length > 0) {
      for (const proc of updatedProcedures) {
        proc.appointment.status = AP_STATUS.paid; // FIXME: shared mutable state
      }

      const firstProcedure = first(filteredProcedures);
      const firstClientId = firstProcedure?.appointment?.client?.id ?? null;
      const notes = firstProcedure?.appointment?.notes;
      const firstAppointmentId = firstProcedure?.appointment?.id;

      const payload = { client_id: firstClientId, procedures: updatedProcedures, notes };

      try {
        checkpoint('initTx.updateAppointmentWithProcedures.init', { tag, payload });

        chaosThrow('throwBeforeInitTxUpdateAppointment');

        const result = await call(`${FTAG} > updateAppointmentWithProcedures()`, options?.updateAppointmentWithProcedures, salonId, firstAppointmentId, payload);

        checkpoint('initTx.updateAppointmentWithProcedures.pass', {
          tag,
          result,
          firstAppointmentId,
          payload,
          message: 'updating appointment procedures succeeded',
        });
      } catch (error) {
        L.warn(FTAG, 'updateAppointmentWithProcedures error:', error);
        checkpoint('initTx.updateAppointmentWithProcedures.error', {
          tag,
          error,
          firstAppointmentId,
          payload: fromAnyToUglyJson(payload),
        });
      }
    } // if filteredProcedures is full

    if (alen(appointmentIds)) {
      // FIXME: shared mutable state, might be a problem, try copying it like {...item} instead
      depositListData = asArray(options?.data?.depositListData).map((item) => Object.assign(item, { depositType: 'used', used_for_appointmentIds: appointmentIds }));
    } // if appointmentIds is full
  } // for apId

  const paymentIntentId = options?.paymentIntentIdRef?.current;
  if (!paymentIntentId) {
    L.warn(FTAG, 'Invalid paymentIntentId:', paymentIntentId);
    checkpoint('initTx.paymentIntentId', { tag, paymentIntentId });
  }

  const txOptions = {
    ...asObject(options?.data),
    depositListData,
    salonId,
    charges: options?.charges,
    appointmentDetails: options?.appointmentDetails,
    paymentIntentId,
    paymentType,
  };

  const isFailInitTxDataValidation = chaosTrue('failInitTxDataValidation');
  if (isFailInitTxDataValidation) txOptions.partCash = txOptions.partCash + 1;

  L.debug(FTAG, 'txOptions:', txOptions);

  const validationResult = validateCheckoutData(txOptions);

  if (!validationResult.isValid) {
    const info = { tag, validationResult, txOptions, message: M_INVALID_DATA, isFailInitTxDataValidation };
    L.info(FTAG, M_INVALID_DATA, 'validationResult:', validationResult);
    checkpoint('initTx.mismatch.fail', info);

    {
      const areTotalsMatchedRef = options?.areTotalsMatchedRef;
      const setTotalsMatched = options?.setTotalsMatched;

      const tag = 'initTx.openMismatchDialog';
      const value = false;
      areTotalsMatchedRef.current = value;
      setTotalsMatched(value);
      checkpoint('areTotalsMatched.nay', { tag, 'areTotalsMatched.value': value });
    }

    // TODO: should transaction continue if validation fails?
  }

  checkpoint('initTx.beCreateTransaction.init', { tag, validationResult, txOptions });

  const setErrorMessage = isFn(options?.setErrorMessage) ? options?.setErrorMessage : alwaysNoop;
  try {
    setErrorMessage(S.empty);

    chaosThrow('throwBeforeInitTxCreateTransaction');

    const result = await call(`${FTAG} > beCreateTransaction()`, options?.beCreateTransaction, txOptions);

    const isFailCreateTransaction = chaosTrue('failCreateTransaction');
    const isOk = isFailCreateTransaction ? false : result.is.ok;

    const errorMessage = isOk ? S.empty : t(EMSG.txCreateTransKey);
    const json = result?.json;
    const info = { tag, isOk, result, validationResult, errorMessage, txOptions, json, isFailCreateTransaction };

    if (isOk) {
      checkpoint('initTx.beCreateTransaction.pass', info, { message: M_CREATE_TX_PASS });
      L.debug(FTAG, 'isOk:', isOk, 'info:', info, 'result:', result, 'new errorMessage:', errorMessage);
    } else {
      checkpoint('initTx.beCreateTransaction.fail', info, { message: M_CREATE_TX_FAIL });
      L.warn(FTAG, 'isOk:', isOk, 'info:', info, 'result:', result, 'new errorMessage:', errorMessage);
    }

    setErrorMessage(errorMessage);
    return isOk;
  } catch (error) {
    L.warn(FTAG, error);
    setErrorMessage(t(EMSG.txCreateTransKey));
    checkpoint('initTx.beCreateTransaction.error', { tag, error, validationResult });

    throw error;
  }
};

const completeTx = async (options) => {
  const FTAG = 'completeTx()';
  const tag = ct(FTAG, options?.tag);

  const { hasAppointmentIds, checkpoint, appointmentIds, changeAppointmentsStatus, salonId, finishUrl, isWeek, history, stylistId } = options ?? {};

  if (hasAppointmentIds) {
    checkpoint('completeTx.changeAppointmentsStatus.init', { tag, hasAppointmentIds });

    const stubAppointments = appointmentIds.map((id) => ({
      id,
      status: 'paid',
      action: '',
      amount: 0,
      deposit_source: '',
      is_repeating: false,
    }));

    try {
      chaosThrow('throwBeforeCompleteTxChangeStatus');

      const result = await changeAppointmentsStatus(salonId, stubAppointments);
      checkpoint('completeTx.changeAppointmentsStatus.pass', {
        tag,
        result: fromAnyToJsonClone(result),
        stubAppointments,
        message: 'change appointment status succeeded',
      });
    } catch (error) {
      L.warn(FTAG, 'completeTx()', 'changeAppointmentsStatus error:', error);
      checkpoint('completeTx.changeAppointmentsStatus.error', { tag, error, stubAppointments });
    }
  }

  if (finishUrl && !isWeek) {
    checkpoint('completeTx.finishUrl.done', { tag, finishUrl });
    navFor(CTAG, FTAG, { isWeek, finishUrl, salonId }, { history }).push(finishUrl);
    return;
  }

  const url = isWeek && stylistId ? linkTo({ pattern: LINK.checkoutCompleteWeek, params: { salonId, stylistId } }) : linkTo({ pattern: LINK.checkoutCompleteDay, params: { salonId } });

  checkpoint('completeTx.noFinishUrl.done', { tag, url, salonId, stylistId });
  navFor(CTAG, FTAG, { isWeek, stylistId, salonId }, { history }).push(url);
};

export const performTx = async ({
  tag: tagFromOptions,
  checkpoint,
  onlineAmount,
  clients,
  paymentIntentIdRef,
  isFetching,
  isTxInitiatedRef,
  checkoutOption,
  cardAmountFromData,
  charges,
  onlineAmountFromData,
  posFlow,
  procedures,
  data,
  feChangePaymentType,
  appointmentIds,
  updateAppointmentWithProcedures,
  salonId,
  appointmentDetails,
  areTotalsMatchedRef,
  setTotalsMatched,
  setErrorMessage,
  beCreateTransaction,
  hasAppointmentIds,
  changeAppointmentsStatus,
  finishUrl,
  isWeek,
  history,
  stylistId,
  t,
}) => {
  const FTAG = 'performTx()';
  const tag = ct(FTAG, tagFromOptions);

  try {
    checkpoint('performTx.init', { tag, onlineAmount, clients });
    // NOTE: on abort, initTx will return false, otherwise it may simply throw error before complete is called

    const initResult = await initTx({
      paymentIntentIdRef,
      checkpoint,
      isFetching,
      isTxInitiatedRef,
      checkoutOption,
      cardAmountFromData,
      charges,
      onlineAmountFromData,
      posFlow,
      procedures,
      data,
      feChangePaymentType,
      appointmentIds,
      updateAppointmentWithProcedures,
      salonId,
      appointmentDetails,
      areTotalsMatchedRef,
      setTotalsMatched,
      setErrorMessage,
      beCreateTransaction,
      t,
    });

    const isFailPerformTxInit = chaosTrue('failPerformTxInit');
    if (!initResult || isFailPerformTxInit) {
      checkpoint('performTx.initTx.fail', { tag, onlineAmount, clients, initResult, isFailPerformTxInit });
      return isFailPerformTxInit ? false : initResult;
    }

    const completeResult = await completeTx({
      hasAppointmentIds,
      checkpoint,
      appointmentIds,
      changeAppointmentsStatus,
      salonId,
      finishUrl,
      isWeek,
      history,
      stylistId,
    });

    const isFailPerformTxComplete = chaosTrue('failPerformTxComplete');

    const info = { tag, onlineAmount, clients, initResult, completeResult, isFailPerformTxComplete };
    checkpoint('performTx.completeTx.done', info);

    return isFailPerformTxComplete ? false : completeResult;
  } catch (error) {
    checkpoint('performTx.error', { tag, onlineAmount, clients, error });
    throw error;
  }
};
